import React from 'react'
import { Link } from 'react-router-dom'
import { useSearchParams } from 'react-router-dom'
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { getYearOptions, exportToCsv } from '../../utils/registryExport'
import SmrFormPanelV2ReadOnly from './components/SmrFormPanelV2ReadOnly'
import PatronIdentityCell from './components/PatronIdentityCell'
import { resolvePatronIdentity } from '../../utils/patronIdentity'
import HelpScreenButton from '../../components/help/HelpScreenButton'
import ScreenHelpPanel from '../../components/help/ScreenHelpPanel'

const SMR_REGISTRY_HELP_SCREEN_KEY = 'page:compliance-safe/smr-registry'

function isArchivalEntry(row) {
  if (String(row?.case_type || '').toUpperCase() === 'ARCHIVAL_SMR') return true
  const meta = row?.registry_meta
  if (meta && typeof meta === 'object' && meta.entry_kind === 'archival') return true
  return false
}

export default function SMR_ECDD_Registry() {
  const [searchParams, setSearchParams] = useSearchParams()
  const caseIdFromUrl = searchParams.get('caseId')
  const yearFromUrl = searchParams.get('year') || ''
  const [items, setItems] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState(null)
  const [venues, setVenues] = React.useState([])
  const [filters, setFilters] = React.useState({
    patron_id: '',
    member_id: '',
    visitor_id: '',
    subject_type: '',
    search: '',
    venue_id: '',
    year: yearFromUrl || '',
  })
  const [previewCase, setPreviewCase] = React.useState(null)
  const [previewSubmission, setPreviewSubmission] = React.useState(null)
  const [previewLoading, setPreviewLoading] = React.useState(false)
  const [previewSubmissionError, setPreviewSubmissionError] = React.useState(null)
  const [registryHint, setRegistryHint] = React.useState(null)
  const [helpPanelOpen, setHelpPanelOpen] = React.useState(false)
  const previewIdentity = React.useMemo(
    () => (previewCase ? resolvePatronIdentity(previewCase) : null),
    [previewCase]
  )

  React.useEffect(() => {
    authFetch(getLocalApiUrl('/api/venues'))
      .then(r => r.json())
      .then(j => { if (j.success) setVenues(j.data || []) })
      .catch(() => {})
  }, [])

  React.useEffect(() => {
    if (yearFromUrl && yearFromUrl !== filters.year) {
      setFilters(prev => ({ ...prev, year: yearFromUrl }))
    }
  }, [yearFromUrl])

  const setYear = (y) => {
    setFilters(prev => ({ ...prev, year: y }))
    const next = new URLSearchParams(searchParams)
    if (y) next.set('year', y)
    else next.delete('year')
    setSearchParams(next, { replace: true })
  }

  const load = React.useCallback(async () => {
    try {
      setLoading(true); setError(null)
      const qs = new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([, v]) => v)))
      const url = getLocalApiUrl(`/api/registry/forms?${qs}&limit=500`)
      const r = await authFetch(url)
      const j = await r.json()
      if (!j.success) throw new Error(j.error || 'Failed to load registry')
      setItems(j.data || [])
      setRegistryHint(j.hint || null)
    } catch (e) { setError(e.message) } finally { setLoading(false) }
  }, [filters])

  React.useEffect(() => { load() }, [load])

  const handleExport = () => {
    const cols = [
      { header: 'Patron ID', accessor: r => resolvePatronIdentity(r).patron_id || '' },
      { header: 'Type', accessor: r => resolvePatronIdentity(r).subject_type },
      { header: 'Member #', accessor: r => resolvePatronIdentity(r).membership_number || resolvePatronIdentity(r).member_id || '' },
      { header: 'Visitor ID', accessor: r => resolvePatronIdentity(r).visitor_id || '' },
      { header: 'Name', accessor: r => r.member_name || '' },
      { header: 'Case Number', accessor: r => r.case_number || r.case_id },
      { header: 'Venue', accessor: r => r.venue_name ? `${r.venue_name}${r.venue_code ? ` (${r.venue_code})` : ''}` : '' },
      { header: 'Severity', accessor: r => r.severity || '' },
      { header: 'Filed', accessor: r => r.last_submitted_at ? new Date(r.last_submitted_at).toLocaleString() : '' },
      { header: 'Submitted By', accessor: r => r.submitted_by || '' },
    ]
    const suffix = filters.year ? `-${filters.year}` : ''
    exportToCsv(items, cols, `smr-registry${suffix}.csv`)
  }

  React.useEffect(() => {
    if (!caseIdFromUrl || items.length === 0 || previewCase) return
    const row = items.find(r => (r.case_id && String(r.case_id) === caseIdFromUrl) || (r.case_number && String(r.case_number) === caseIdFromUrl))
    if (row) {
      openPreview(row)
      const next = new URLSearchParams(searchParams)
      next.delete('caseId')
      setSearchParams(next, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseIdFromUrl, items, previewCase])

  const openPreview = async (row) => {
    setPreviewCase(row)
    setPreviewSubmission(null)
    setPreviewSubmissionError(null)
    setPreviewLoading(true)
    try {
      const cid = encodeURIComponent(row.case_id)
      const subRes = await authFetch(getLocalApiUrl(`/api/cases/${cid}/forms/SMR/submissions`)).then(r => r.json())
      const rawList = subRes.success ? subRes.data : null
      const submissions = Array.isArray(rawList) ? rawList : []
      if (subRes.success && rawList != null && !Array.isArray(rawList)) {
        setPreviewSubmissionError('Submissions API returned non-array data; cannot resolve filing id.')
        return
      }
      const latest = submissions[0]
      const submissionId = latest != null ? (latest.id ?? latest.submission_id) : null
      if (submissionId == null || submissionId === '') {
        setPreviewSubmissionError(submissions.length === 0
          ? 'No SMR submission row found for this case.'
          : 'Latest submission has no id; cannot load details.')
        return
      }
      const detailUrl = getLocalApiUrl(
        `/api/cases/${encodeURIComponent(row.case_id)}/forms/SMR/submissions/${encodeURIComponent(String(submissionId))}`
      )
      const detailRes = await authFetch(detailUrl)
      const detailJson = await detailRes.json().catch(() => null)
      if (!detailRes.ok || !detailJson?.success || !detailJson.data) {
        setPreviewSubmissionError(detailJson?.error || `Could not load submission (HTTP ${detailRes.status}).`)
        return
      }
      setPreviewSubmission(detailJson.data)
    } catch (e) {
      setPreviewSubmissionError(e?.message || 'Failed to load SMR details.')
    } finally {
      setPreviewLoading(false)
    }
  }

  const closePreview = () => {
    setPreviewCase(null)
    setPreviewSubmission(null)
    setPreviewSubmissionError(null)
  }

  const submissionPayload = previewSubmission?.data != null && typeof previewSubmission.data === 'object'
    ? previewSubmission.data
    : null

  return (
    <div className="max-w-6xl mx-auto">
      <div className="card mb-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold">SMR Registry</h1>
          <div className="flex items-center gap-3">
            <HelpScreenButton
              screenKey={SMR_REGISTRY_HELP_SCREEN_KEY}
              onClick={() => setHelpPanelOpen(true)}
            />
            <span className="text-sm text-gray-500">{loading ? 'Loading…' : `${items.length} filed SMR${items.length !== 1 ? 's' : ''}`}</span>
            <button type="button" onClick={handleExport} className="btn-secondary flex items-center gap-1.5 px-3 py-1.5 text-sm" disabled={loading || items.length === 0}>
              <ArrowDownTrayIcon className="w-4 h-4" />
              Export
            </button>
            <Link to="/compliance-safe/smr-registry/archival" className="btn-primary px-3 py-1.5 text-sm">
              Register historical SMR
            </Link>
          </div>
        </div>
        <p className="text-sm text-gray-600 mt-1">Suspicious Matter Reports filed and retained as immutable submissions (7-year retention).</p>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Year</label>
            <select className="select-field w-full" value={filters.year} onChange={(e) => setYear(e.target.value)}>
              <option value="">All years</option>
              {getYearOptions().map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Venue</label>
            <select className="select-field w-full" value={filters.venue_id} onChange={(e) => setFilters(prev => ({ ...prev, venue_id: e.target.value }))}>
              <option value="">All Venues</option>
              {venues.map(v => (
                <option key={v.id} value={v.id}>{v.name} ({v.code})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Patron / Master profile ID</label>
            <input className="input-field w-full font-mono text-sm" value={filters.patron_id} onChange={(e) => setFilters(prev => ({ ...prev, patron_id: e.target.value }))} placeholder="PAT-…" />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Member #</label>
            <input className="input-field w-full font-mono text-sm" value={filters.member_id} onChange={(e) => setFilters(prev => ({ ...prev, member_id: e.target.value }))} placeholder="Club member #" />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Visitor ID</label>
            <input className="input-field w-full font-mono text-sm" value={filters.visitor_id} onChange={(e) => setFilters(prev => ({ ...prev, visitor_id: e.target.value }))} placeholder="VMS visitor ref" />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Subject type</label>
            <select className="select-field w-full" value={filters.subject_type} onChange={(e) => setFilters(prev => ({ ...prev, subject_type: e.target.value }))}>
              <option value="">All</option>
              <option value="member">Member</option>
              <option value="visitor">Visitor</option>
            </select>
          </div>
          <div className="md:col-span-2 lg:col-span-1">
            <label className="block text-xs text-gray-600 mb-1">Search</label>
            <input className="input-field w-full" placeholder="Case #, name, venue…" value={filters.search} onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))} />
          </div>
        </div>
      </div>
      <div className="card">
        {error && <div className="text-sm text-red-600 mb-2">{error}</div>}
        {registryHint && (
          <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-3" role="status">
            {registryHint}
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left">Patron</th>
                <th className="px-3 py-2 text-left">Case</th>
                <th className="px-3 py-2 text-left">Venue</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">AUSTRAC ref</th>
                <th className="px-3 py-2 text-left">Lodged</th>
                <th className="px-3 py-2 text-left">Registered</th>
                <th className="px-3 py-2 text-left">Submitted By</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {items.map(row => {
                const archival = isArchivalEntry(row)
                const lodged = row.austrac_filed_at || row.registry_meta?.historical_filed_at
                return (
                <tr key={row.case_id}>
                  <td className="px-3 py-2"><PatronIdentityCell row={row} /></td>
                  <td className="px-3 py-2 font-mono">{row.case_number || row.case_id}</td>
                  <td className="px-3 py-2">{row.venue_name ? `${row.venue_name}${row.venue_code ? ` (${row.venue_code})` : ''}` : '—'}</td>
                  <td className="px-3 py-2">
                    {archival ? (
                      <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-violet-100 text-violet-800">Archival</span>
                    ) : (
                      <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Live</span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{row.austrac_reference || row.registry_meta?.austrac_reference || '—'}</td>
                  <td className="px-3 py-2 text-xs">{lodged ? new Date(lodged).toLocaleString() : '—'}</td>
                  <td className="px-3 py-2 text-xs">{row.last_submitted_at ? new Date(row.last_submitted_at).toLocaleString() : '—'}</td>
                  <td className="px-3 py-2 text-gray-600">{row.submitted_by || '—'}</td>
                  <td className="px-3 py-2 text-right">
                    <button className="btn-secondary" onClick={() => openPreview(row)}>View</button>
                  </td>
                </tr>
                )
              })}
              {items.length === 0 && !loading && (
                <tr><td className="px-3 py-8 text-gray-500 text-center" colSpan={9}>No filed SMRs. SMRs appear here once they have been submitted.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {previewCase && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-2 sm:p-4 bg-black/40 overflow-y-auto" onClick={closePreview}>
          <div className="w-full max-w-6xl bg-white rounded-lg shadow-xl my-4 max-h-[98vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b flex items-center justify-between flex-shrink-0 bg-gray-50">
              <div>
                <div className="text-xs text-gray-500">SMR Registry — filed submission</div>
                <div className="text-lg font-semibold flex items-center gap-2">
                  {previewCase.case_number || `CASE-${previewCase.case_id}`}
                  {isArchivalEntry(previewCase) ? (
                    <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-violet-100 text-violet-800">Archival</span>
                  ) : null}
                </div>
              </div>
              <button type="button" className="btn-secondary" onClick={closePreview}>Close</button>
            </div>
            <div className="p-3 sm:p-4 overflow-y-auto flex-1">
              {previewLoading ? (
                <div className="py-16 text-center text-gray-500">Loading filed SMR…</div>
              ) : previewSubmissionError ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-900 text-sm px-4 py-3">{previewSubmissionError}</div>
              ) : (
                <SmrFormPanelV2ReadOnly
                  draft={submissionPayload}
                  caseNumber={previewCase.case_number || String(previewCase.case_id)}
                  patronName={previewCase.member_name || ''}
                  memberId={previewIdentity?.member_id || ''}
                  visitorId={previewIdentity?.visitor_id || ''}
                  masterProfileId={previewIdentity?.master_profile_id || previewIdentity?.patron_id || ''}
                  subjectType={previewIdentity?.subject_type}
                  filedAt={previewSubmission?.submitted_at || previewCase.last_submitted_at}
                  submittedBy={previewSubmission?.submitted_by || previewCase.submitted_by}
                  submissionId={previewSubmission?.id}
                />
              )}
            </div>
          </div>
        </div>
      )}
      <ScreenHelpPanel
        screenKey={SMR_REGISTRY_HELP_SCREEN_KEY}
        open={helpPanelOpen}
        onClose={() => setHelpPanelOpen(false)}
        sectionIdPrefix="smr-registry-help"
      />
    </div>
  )
}
